import { isEnrolled, type AppState, type EnrolmentState } from "../state/store";

/**
 * Which screen a given state is allowed to be on, as a pure function.
 *
 * Its own module so the policy can be asserted without a router or a DOM -- the same reason
 * `engine/wrapup.ts` is one. [V] It earned that: the policy shipped with a rule nothing
 * could see, and the deployments it broke were the ones nobody develops against.
 */

/** What a route requires of the state before it will render. */
export type Need = "enrolled" | "unenrolled" | "signed-in" | "signed-out" | "session";

/**
 * Exactly the fields the policy reads. Anything wider makes it harder to test than to run.
 *
 * The two Welcome fields are optional: a state that says nothing about them is not owed the
 * Welcome.
 */
export type GuardState = EnrolmentState &
  Pick<AppState, "account" | "session"> &
  Partial<Pick<AppState, "welcomeDone" | "launchPlain">>;

/**
 * Whether this launch opens on the first-run Welcome (#1095) rather than Sign in.
 *
 * Only a device with nothing on it: signed out, with no credentials, voice or key (a
 * half-set-up device is an existing user too), that has not tapped Get started, and only on
 * a plain launch, so a magic link, a notes link or any other entry link goes where it goes.
 */
export function welcomeDue(state: GuardState): boolean {
  if (state.account || state.credentials || state.template || state.keypair) return false;
  return state.welcomeDone === false && state.launchPlain === true;
}

/**
 * Where a launch lands: **sign-in first, enrolment second, home third** -- with the
 * first-run Welcome in front of sign-in on a fresh device (`welcomeDue`).
 *
 * Signing in is the front door rather than a thing to discover later. The order matters
 * beyond tidiness: the account has to exist before the device does, or enrolment mints a
 * device with no owner and the app has to reconcile them afterwards.
 *
 * The cost, stated because it is real: enrolment is needed to *join* a session too, not
 * only to start one, so gating it behind sign-in asks an invited guest for an account as
 * well. #344 makes that the whole story: the server refuses anonymous joins too, so the
 * account is asked for before enrolment, not at the door of a session.
 */
export function firstScreen(state: GuardState): string {
  if (!state.account) return welcomeDue(state) ? "/welcome" : "/signin";
  return isEnrolled(state) ? "/" : "/enroll";
}

/**
 * The redirect a screen owes this state, or `null` to render it.
 *
 * **Signed out, the front door is the only screen.** Every need but `signed-out` asks for
 * an account first, whatever the device holds: an enrolled device that signs out lands on
 * sign-in (home may open on the first-run Welcome, `firstScreen`) and nothing gets it off that door but
 * signing in. There is no way past it -- no "continue without signing in", no signed-out
 * Home, Settings or result -- the same rule the iOS and Android apps keep.
 */
export function redirectFor(need: Need, state: GuardState): string | null {
  const signedIn = Boolean(state.account);

  if (need === "signed-out") return signedIn ? firstScreen(state) : null;
  // Home is the launch screen, so it alone may open on the first-run Welcome instead.
  if (!signedIn) return need === "enrolled" ? firstScreen(state) : "/signin";

  const enrolled = isEnrolled(state);
  if (need === "enrolled" && !enrolled) return firstScreen(state);
  if (need === "unenrolled" && enrolled) return "/";
  // A live session is in memory, never in the URL. Landing here without one -- a reload, a
  // shared link, the back button after it ended -- is not an error, it is just nowhere.
  if (need === "session" && !state.session) return "/";

  return null;
}

/**
 * Where an old notes link goes: `/?session=<id>` becomes `/session/<id>/result`.
 *
 * The result email and push used to link to `/?session=<id>`, and mail already sent keeps
 * that link forever. They now link to the result route the apps claim as an App Link
 * (#498). Checked before the home guard runs, so the id is not lost on the way: the result
 * route's guard then keeps it for after sign-in (`state/return-to.ts`).
 *
 * Only a well-formed session id redirects (the server's `ses_` + 24 hex, `models._uid`);
 * anything else is a plain home URL, so a crafted link cannot steer the redirect.
 */
export function legacyResultPath(search: string): string | null {
  const id = new URLSearchParams(search).get("session");
  return id && SESSION_ID.test(id) ? `/session/${encodeURIComponent(id)}/result` : null;
}

const SESSION_ID = /^ses_[0-9a-f]{24}$/;
